import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useScroll } from "../../scroll/ScrollProvider";

/**
 * ComputeCore — sovereign-grade compute motif:
 * a central quantum core, orbital rings, and chip towers.
 * Used for Section 6 (Data Centers & Compute).
 */
export function ComputeCore() {
  const root = useRef<THREE.Group>(null);
  const ringA = useRef<THREE.Mesh>(null);
  const ringB = useRef<THREE.Mesh>(null);
  const ringC = useRef<THREE.Mesh>(null);
  const { section, sectionProgress } = useScroll();

  const towers = useMemo(() => {
    const out: { x: number; z: number; h: number }[] = [];
    for (let x = -2; x <= 2; x++) {
      for (let z = -2; z <= 2; z++) {
        if (x === 0 && z === 0) continue;
        const d = Math.sqrt(x * x + z * z);
        out.push({ x: x * 0.7, z: z * 0.7, h: 0.25 + Math.max(0, 1.2 - d) * 0.45 });
      }
    }
    return out;
  }, []);

  useFrame((_state, delta) => {
    const active = section < 6 ? 0 : section === 6 ? sectionProgress : 1;
    if (root.current) {
      root.current.rotation.y += delta * (0.08 + active * 0.14);
      root.current.position.y = Math.sin(performance.now() * 0.00045) * 0.08;
    }
    if (ringA.current) ringA.current.rotation.z += delta * 0.18;
    if (ringB.current) ringB.current.rotation.z -= delta * 0.14;
    if (ringC.current) ringC.current.rotation.z += delta * 0.1;
  });

  return (
    <group ref={root}>
      {/* Saturn-like planet body */}
      <mesh castShadow>
        <sphereGeometry args={[0.86, 72, 48]} />
        <meshStandardMaterial
          color="#d8b277"
          metalness={0.35}
          roughness={0.6}
          emissive="#2f2212"
          emissiveIntensity={0.24}
        />
      </mesh>

      {/* Latitudinal cloud bands to evoke Saturn texture */}
      {Array.from({ length: 8 }).map((_, i) => {
        const y = -0.62 + i * 0.18;
        const r = Math.max(0.1, Math.sqrt(Math.max(0, 0.86 * 0.86 - y * y)));
        const warm = i % 2 === 0;
        return (
          <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[r, 0.012, 10, 96]} />
            <meshStandardMaterial
              color={warm ? "#e5c184" : "#a78960"}
              metalness={0.2}
              roughness={0.72}
              emissive={warm ? "#2f210f" : "#1b1815"}
              emissiveIntensity={0.16}
            />
          </mesh>
        );
      })}

      {/* Main ring system */}
      <mesh ref={ringA} rotation={[Math.PI * 0.52, 0.38, 0]}>
        <torusGeometry args={[1.36, 0.05, 18, 180]} />
        <meshStandardMaterial color="#e7c98a" metalness={0.9} roughness={0.28} emissive="#2a1d08" emissiveIntensity={0.24} />
      </mesh>
      <mesh ref={ringB} rotation={[Math.PI * 0.52, 0.38, 0]}>
        <torusGeometry args={[1.58, 0.022, 14, 160]} />
        <meshStandardMaterial color="#8ba3ff" metalness={0.95} roughness={0.34} emissive="#132045" emissiveIntensity={0.24} />
      </mesh>
      <mesh ref={ringC} rotation={[Math.PI * 0.52, 0.38, 0]}>
        <torusGeometry args={[1.22, 0.016, 10, 120]} />
        <meshStandardMaterial color="#f0d7a5" metalness={0.85} roughness={0.38} emissive="#2b220f" emissiveIntensity={0.18} />
      </mesh>

      {towers.map((t, i) => (
        <mesh key={i} position={[t.x, -0.95 + t.h / 2, t.z]} castShadow>
          <boxGeometry args={[0.22, t.h, 0.22]} />
          <meshStandardMaterial color="#141722" metalness={0.85} roughness={0.35} />
        </mesh>
      ))}

      {/* Small "compute satellites" */}
      {Array.from({ length: 3 }).map((_, i) => {
        const a = (i / 3) * Math.PI * 2 + 0.8;
        return (
          <mesh key={`sat-${i}`} position={[Math.cos(a) * 2.1, 0.15 + (i - 1) * 0.12, Math.sin(a) * 2.1]}>
            <icosahedronGeometry args={[0.08, 0]} />
            <meshStandardMaterial color="#8aa7ff" emissive="#364e9f" emissiveIntensity={0.45} metalness={1} roughness={0.22} />
          </mesh>
        );
      })}

      <pointLight position={[0, 0.45, 0.2]} intensity={1.0} color="#88a7ff" distance={5} decay={2} />
      <pointLight position={[0.9, 0.2, 1.2]} intensity={0.9} color="#f1cd8b" distance={6} decay={2} />
    </group>
  );
}
